import {
  __name,
  __objRest,
  __spreadValues
} from "./chunk-PSMXLSLO.jsx";

// atoms/box/index.tsx
import { forwardRef as forwardRefDefault } from "react";
import { css as css2, cx } from "@headout/pixie/css";

// utils/splitCssProps.ts
import { css } from "@headout/pixie/css";
import { splitCssProps as splitCssPropsPixie } from "@headout/pixie/jsx";
var splitCssProps = /* @__PURE__ */ __name((props) => {
  const [cssProps, restProps] = splitCssPropsPixie(props);
  const _a = cssProps, { css: cssProp } = _a, styleProps = __objRest(_a, ["css"]);
  const cssRaw = css.raw(
    styleProps,
    ...Array.isArray(cssProp) ? cssProp : [cssProp]
  );
  return {
    restProps,
    cssRaw
  };
}, "splitCssProps");

// atoms/box/index.tsx
var forwardRef = forwardRefDefault;
var Box = forwardRef(/* @__PURE__ */ __name(function Box2(_a, ref) {
  var _b = _a, { as } = _b, props = __objRest(_b, ["as"]);
  const { restProps, cssRaw } = splitCssProps(props);
  const Element = as || "div";
  return <Element
    {...restProps}
    className={cx(css2(cssRaw), props.className)}
    ref={ref}
  />;
}, "Box"));

// atoms/button/index.tsx
import { forwardRef as forwardRef2, useRef as useRef2 } from "react";
import { css as css5, cx as cx3 } from "@headout/pixie/css";

// atoms/text/index.tsx
import { css as css3, cx as cx2 } from "@headout/pixie/css";

// atoms/text/styles.tsx
import { cva } from "@headout/pixie/css";
var textRecipe = cva({
  base: {
    padding: 0,
    margin: 0
  },
  variants: {
    align: {
      center: {
        textAlign: "center"
      },
      inherit: {
        textAlign: "inherit"
      },
      justify: {
        textAlign: "justify"
      },
      left: {
        textAlign: "left"
      },
      right: {
        textAlign: "right"
      }
    },
    variant: {
      normal: {},
      italic: {
        fontStyle: "italic"
      }
    }
  },
  defaultVariants: {
    align: "inherit",
    variant: "normal"
  }
});

// atoms/text/index.tsx
var Text = /* @__PURE__ */ __name((_a) => {
  var _b = _a, {
    as: Component = "p",
    align,
    variant,
    className
  } = _b, rest = __objRest(_b, [
    "as",
    "align",
    "variant",
    "className"
  ]);
  const Element = Component;
  const recipeClasses = textRecipe.raw({ align, variant });
  const { restProps, cssRaw } = splitCssProps(rest);
  return <Element
    {...restProps}
    className={cx2(css3(recipeClasses, cssRaw), className)}
  />;
}, "Text");

// atoms/button/buttonLoader.tsx
import { useEffect, useRef } from "react";
import { css as css4 } from "@headout/pixie/css";
var ButtonLoader = /* @__PURE__ */ __name(({
  state = "default",
  styles,
  containerRef,
  hasLoadingAnimation = false
}) => {
  const loaderRef = useRef(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!hasLoadingAnimation || !container) return;
    if (state === "loading") {
      const loader = loaderRef.current;
      if (loader) {
        container.style.top = "-50px";
        setTimeout(() => {
          loader.style.top = "50%";
          loader.style.transform = "translate(-50%, -50%)";
        }, 300);
      }
    } else {
      container.style.top = "0";
    }
  }, [containerRef, hasLoadingAnimation, state]);
  if (state !== "loading") return null;
  return <span
    ref={loaderRef}
    className={css4(styles)}
    role='progressbar'
    aria-label='loading'
    aria-live='assertive'
  >
			<svg
    version='1.1'
    id='L5'
    xmlns='http://www.w3.org/2000/svg'
    xmlnsXlink='http://www.w3.org/1999/xlink'
    x='0px'
    y='0px'
    viewBox='0 0 100 100'
    enableBackground='new 0 0 0 0'
    xmlSpace='preserve'
    width={"100%"}
    height={"100%"}
  >
				<circle
    fill='currentColor'
    stroke='none'
    cx='10'
    cy='50'
    r='10'
  >
					<animateTransform
    attributeName='transform'
    dur='.7s'
    type='translate'
    values='0 6 ; 0 -6; 0 6'
    repeatCount='indefinite'
    begin='0.1'
  />
				</circle>
				<circle
    fill='currentColor'
    stroke='none'
    cx='50'
    cy='50'
    r='10'
  >
					<animateTransform
    attributeName='transform'
    dur='.7s'
    type='translate'
    values='0 6 ; 0 -6; 0 6'
    repeatCount='indefinite'
    begin='0.25'
  />
				</circle>
				<circle
    fill='currentColor'
    stroke='none'
    cx='90'
    cy='50'
    r='10'
  >
					<animateTransform
    attributeName='transform'
    dur='.7s'
    type='translate'
    values='0 6 ; 0 -6; 0 6'
    repeatCount='indefinite'
    begin='0.4'
  />
				</circle>
			</svg>
		</span>;
}, "ButtonLoader");

// atoms/button/styles.tsx
import { sva } from "@headout/pixie/css";
var buttonRecipe = sva({
  slots: [
    "root",
    "container",
    "loader",
    "icon",
    "primaryText",
    "secondaryText"
  ],
  base: {
    root: {
      display: "flex",
      justifyContent: "center",
      width: "100%",
      border: "1px solid",
      // TODO: tokens
      overflow: "hidden",
      borderRadius: "radius.12",
      _hover: {
        cursor: "pointer",
        transition: "background 0.3s cubic-bezier(0.7, 0, 0.3, 1)"
        // TODO: tokens
      },
      _focusVisible: {
        outline: "none",
        boxShadow: "0 0 0 2px token(colors.semantic.surface.light.white), 0 0 0 3px token(colors.semantic.surface.dark.primary.2)"
      },
      _active: {
        transform: "scale(0.95)",
        transition: "transform 0.2s cubic-bezier(0.7, 0, 0.3, 1)"
        // TODO: tokens
      }
    },
    secondaryText: {
      textStyle: "Semantics/UI Label/Small"
    },
    container: {
      display: "flex"
    },
    loader: {
      display: "inline-block"
    }
  },
  variants: {
    size: {
      small: {
        root: {
          padding: "token(spacing.space.6) token(spacing.space.12) token(spacing.space.6)",
          borderRadius: "radius.8"
        },
        primaryText: {
          textStyle: "Semantics/CTA/Small"
        },
        container: {
          marginBottom: "space.2"
        }
      },
      medium: {
        root: {
          padding: "token(spacing.space.12) token(spacing.space.16)"
        },
        primaryText: {
          textStyle: "Semantics/CTA/Regular"
        },
        secondaryText: {
          marginBottom: "space.2"
        }
      },
      large: {
        root: {
          padding: "token(spacing.space.14) token(spacing.space.16)"
        },
        primaryText: {
          textStyle: "Semantics/CTA/Large"
        },
        secondaryText: {
          marginBottom: "space.2"
        }
      }
    },
    state: {
      default: {},
      loading: {},
      disabled: {}
    },
    direction: {
      row: {
        container: {
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: "space.8"
        }
      },
      rowReverse: {
        container: {
          flexDirection: "row-reverse",
          alignItems: "center",
          justifyContent: "center",
          gap: "space.8"
        }
      },
      column: {
        container: {
          flexDirection: "column",
          alignItems: "center"
        }
      }
    },
    isIconOnly: {
      true: {
        root: {
          padding: 0,
          justifyContent: "center",
          alignItems: "center"
        }
      },
      false: {}
    },
    hasSecondaryText: {
      true: {},
      false: {}
    },
    hasLoadingAnimation: {
      true: {
        root: {
          position: "relative"
        },
        container: {
          position: "relative",
          top: "0",
          transition: "top 0.3s cubic-bezier(0.7, 0, 0.3, 1)"
          // TODO: tokens
        },
        loader: {
          position: "absolute",
          top: "100%",
          left: "50%",
          transform: "translateX('-50%')",
          transition: "top 0.1s cubic-bezier(0.7, 0, 0.3, 1)"
          // TODO: tokens
        }
      },
      false: {
        root: {
          alignItems: "center",
          justifyContent: "center"
        }
      }
    },
    variant: {
      primary: {
        root: {
          color: "semantic.cta.white"
        }
      },
      secondary: {
        root: {
          backgroundColor: "transparent"
        }
      },
      tertiary: {}
    },
    btnType: {
      primary: {},
      black: {},
      white: {},
      transparent: {}
    }
  },
  compoundVariants: [
    // secondaryText with different button sizes.
    {
      size: "medium",
      hasSecondaryText: true,
      css: {
        root: {
          padding: "token(spacing.space.4) token(spacing.space.16)"
        }
      }
    },
    {
      size: "large",
      hasSecondaryText: true,
      css: {
        root: {
          padding: "token(spacing.space.6) token(spacing.space.16)"
        }
      }
    },
    // Spacing when secondary text is absent.
    {
      size: ["medium", "large"],
      hasSecondaryText: false,
      css: {
        primaryText: {
          marginBottom: "space.2"
        }
      }
    },
    // icon with different button sizes.
    {
      size: "small",
      css: {
        icon: {
          width: "12px",
          height: "12px"
        }
      }
    },
    {
      size: ["medium", "large"],
      css: {
        icon: {
          width: "16px",
          height: "16px"
        }
      }
    },
    // common styles of disabled and loading states
    {
      state: ["disabled", "loading"],
      css: {
        root: {
          pointerEvents: "none",
          _active: {
            transform: "none"
          }
        }
      }
    },
    // loading state with different button sizes.
    {
      size: ["small", "medium"],
      state: "loading",
      css: {
        loader: {
          width: "20px",
          height: "20px"
        }
      }
    },
    {
      size: "large",
      state: "loading",
      css: {
        loader: {
          width: "24px",
          height: "24px"
        }
      }
    },
    // icon only with different button sizes.
    {
      size: "small",
      isIconOnly: true,
      css: {
        root: {
          width: "36px",
          height: "36px"
        },
        loader: {
          width: "12px",
          height: "12px"
        }
      }
    },
    {
      size: "medium",
      isIconOnly: true,
      css: {
        root: {
          width: "48px",
          height: "48px"
        },
        loader: {
          width: "16px",
          height: "16px"
        }
      }
    },
    {
      size: "large",
      isIconOnly: true,
      css: {
        root: {
          width: "56px",
          height: "56px"
        },
        loader: {
          width: "16px",
          height: "16px"
        }
      }
    },
    // animation loading state with different sizes.
    {
      hasLoadingAnimation: false,
      state: "loading",
      size: ["medium", "large"],
      css: {
        loader: {
          margin: "token(spacing.space.1) 0"
        }
      }
    },
    // disabled state with different variants
    {
      state: "disabled",
      variant: "primary",
      css: {
        root: {
          color: "semantic.cta.white",
          backgroundColor: "semantic.cta.disabled.primary",
          borderColor: "semantic.border.disabled.1"
        }
      }
    },
    {
      state: "disabled",
      variant: "secondary",
      css: {
        root: {
          color: "semantic.text.disabled",
          borderColor: "semantic.border.disabled.1"
        }
      }
    },
    {
      state: "disabled",
      variant: "tertiary",
      css: {
        root: {
          color: "semantic.text.disabled",
          backgroundColor: "semantic.cta.disabled.tertiary",
          borderColor: "semantic.cta.disabled.tertiary"
        }
      }
    },
    // variant & btnType 'primary'.
    {
      btnType: "primary",
      variant: "primary",
      state: ["default", "loading"],
      css: {
        root: {
          backgroundColor: "semantic.cta.primary",
          borderColor: "semantic.border.primary",
          _hover: {
            backgroundColor: "semantic.border.primary"
          }
        }
      }
    },
    {
      btnType: "primary",
      variant: "secondary",
      state: ["default", "loading"],
      css: {
        root: {
          color: "semantic.text.primary",
          borderColor: "semantic.border.primary",
          _hover: {
            backgroundColor: "semantic.surface.light.primary.1"
          }
        }
      }
    },
    {
      btnType: "primary",
      variant: "tertiary",
      state: ["default", "loading"],
      css: {
        root: {
          color: "semantic.text.primary",
          backgroundColor: "semantic.surface.light.primary.1",
          borderColor: "semantic.surface.light.primary.1",
          _hover: {
            backgroundColor: "semantic.surface.light.primary.2",
            borderColor: "semantic.surface.light.primary.2"
          }
        }
      }
    },
    // variant & btnType 'black'.
    {
      btnType: "black",
      variant: "primary",
      state: ["default", "loading"],
      css: {
        root: {
          backgroundColor: "semantic.cta.black",
          borderColor: "core.primary.black",
          // semantic???
          _hover: {
            backgroundColor: "core.primary.black"
            // semantic???
          }
        }
      }
    },
    {
      btnType: "black",
      variant: "secondary",
      state: ["default", "loading"],
      css: {
        root: {
          color: "semantic.text.grey.1",
          borderColor: "core.primary.black",
          _hover: {
            backgroundColor: "semantic.surface.light.grey.2"
          }
        }
      }
    },
    {
      btnType: "black",
      variant: "tertiary",
      state: ["default", "loading"],
      css: {
        root: {
          color: "semantic.text.grey.1",
          backgroundColor: "semantic.surface.light.grey.2",
          borderColor: "semantic.surface.light.grey.2",
          _hover: {
            backgroundColor: "semantic.surface.light.grey.3",
            borderColor: "semantic.surface.light.grey.3"
          }
        }
      }
    },
    // variant & btnType 'white'.
    {
      btnType: "white",
      variant: "primary",
      state: ["default", "loading"],
      css: {
        root: {
          color: "semantic.text.grey.1",
          backgroundColor: "semantic.cta.white",
          borderColor: "semantic.dividers.dark",
          _hover: {
            backgroundColor: "semantic.surface.light.grey.2"
          }
        }
      }
    },
    {
      btnType: "white",
      variant: "secondary",
      state: ["default", "loading"],
      css: {
        root: {
          color: "core.primary.white",
          // semantic???
          borderColor: "semantic.surface.light.white",
          _hover: {
            backgroundColor: "semantic.overlay.light"
          }
        }
      }
    },
    /* variant & btnType 'white' with disabled state. (only required for 
    'white' color, rest all colors have the same disabled state styles) */
    {
      btnType: "white",
      variant: "primary",
      state: "disabled",
      css: {
        root: {
          color: "semantic.text.grey.1"
        }
      }
    },
    // variant & btnType 'transparent'.
    {
      btnType: "transparent",
      variant: "primary",
      state: ["default", "loading"],
      css: {
        root: {
          color: "semantic.text.grey.1",
          backgroundColor: "transparent",
          borderColor: "transparent",
          _hover: {
            backgroundColor: "core.grey.100"
          }
        }
      }
    },
    {
      btnType: "transparent",
      variant: "tertiary",
      state: ["default", "loading"],
      css: {
        root: {
          color: "semantic.cta.white",
          backgroundColor: "transparent",
          borderColor: "transparent",
          _hover: {
            backgroundColor: "core.black.a10"
          }
        }
      }
    }
  ],
  defaultVariants: {
    state: "default",
    size: "medium",
    direction: "row",
    isIconOnly: false,
    hasSecondaryText: false,
    variant: "primary",
    btnType: "primary"
  }
});

// atoms/button/index.tsx
var Button = forwardRef2(/* @__PURE__ */ __name(function Button2(_a, ref) {
  var _b = _a, {
    variant = "primary",
    btnType = "primary",
    size = "medium",
    state = "default",
    primaryText = "",
    secondaryText,
    icon,
    iconPosition = "leading",
    iconAriaLabel,
    hasLoadingAnimation = true,
    overrideStyles = {},
    onClick,
    as,
    className
  } = _b, rest = __objRest(_b, [
    "variant",
    "btnType",
    "size",
    "state",
    "primaryText",
    "secondaryText",
    "icon",
    "iconPosition",
    "iconAriaLabel",
    "hasLoadingAnimation",
    "overrideStyles",
    "onClick",
    "as",
    "className"
  ]);
  const containerRef = useRef2(null);
  const isIconOnly = Boolean(!primaryText && (icon || state === "loading"));
  const { cssRaw, restProps } = splitCssProps(rest);
  const styles = buttonRecipe.raw({
    btnType,
    size,
    state,
    variant,
    hasSecondaryText: !!secondaryText,
    hasLoadingAnimation: !!hasLoadingAnimation,
    isIconOnly: !!isIconOnly,
    direction: icon ? iconPosition === "trailing" ? "rowReverse" : "row" : "column"
  });
  const componentProps = __spreadValues({
    className: cx3(
      css5(
        styles.root,
        cssRaw,
        overrideStyles == null ? void 0 : overrideStyles["root"]
      ),
      className
    ),
    "aria-label": primaryText
  }, restProps);
  const ButtonContentWithProps = <ButtonContent
    state={state}
    primaryText={primaryText}
    secondaryText={secondaryText}
    icon={icon}
    isIconOnly={isIconOnly}
    iconAriaLabel={iconAriaLabel}
    hasLoadingAnimation={hasLoadingAnimation}
    styles={styles}
    overrideStyles={overrideStyles}
    containerRef={containerRef}
  />;
  if (as === "anchor") {
    const handleKeyDown = /* @__PURE__ */ __name((event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick == null ? void 0 : onClick(
          event
        );
      }
    }, "handleKeyDown");
    return <a
      ref={ref}
      tabIndex={0}
      role='button'
      onClick={onClick}
      onKeyDown={handleKeyDown}
      {...componentProps}
    >
				{ButtonContentWithProps}
			</a>;
  }
  return <button
    ref={ref}
    onClick={onClick}
    disabled={state === "disabled"}
    {...componentProps}
  >
			{ButtonContentWithProps}
		</button>;
}, "Button"));
var ButtonContent = /* @__PURE__ */ __name(({
  state,
  primaryText,
  secondaryText,
  icon,
  iconAriaLabel,
  hasLoadingAnimation,
  styles,
  isIconOnly,
  overrideStyles,
  containerRef
}) => <>
		{(state !== "loading" || hasLoadingAnimation) && <span
  className={css5(
    styles.container,
    overrideStyles == null ? void 0 : overrideStyles["container"]
  )}
  ref={containerRef}
>
				{icon && <span
  className={css5(
    styles.icon,
    overrideStyles == null ? void 0 : overrideStyles["icon"]
  )}
  role='img'
  aria-label={iconAriaLabel}
  aria-hidden={!iconAriaLabel}
>
						{icon}
					</span>}
				{!isIconOnly && <Text
  as={"span"}
  className={css5(
    styles.primaryText,
    overrideStyles == null ? void 0 : overrideStyles["primaryText"]
  )}
>
						{primaryText}
					</Text>}
				{secondaryText && <Text
  as={"span"}
  className={css5(
    styles.secondaryText,
    overrideStyles == null ? void 0 : overrideStyles["secondaryText"]
  )}
>
						{secondaryText}
					</Text>}
			</span>}

		<ButtonLoader
  state={state}
  styles={styles.loader}
  containerRef={containerRef}
  hasLoadingAnimation={hasLoadingAnimation}
/>
	</>, "ButtonContent");

// atoms/link/index.tsx
import { css as css6, cx as cx4 } from "@headout/pixie/css";

// atoms/link/styles.tsx
import { sva as sva2 } from "@headout/pixie/css";
var linkRecipe = sva2({
  slots: ["root", "container", "text", "leadingIcon", "trailingIcon"],
  base: {
    root: {
      width: "fit-content",
      display: "flex",
      alignItems: "center",
      gap: "space.4",
      _hover: {
        cursor: "pointer",
        color: "semantic.link.primary",
        transition: "background 0.3s cubic-bezier(0.7, 0, 0.3, 1)"
        // TODO: tokens
      },
      _focusVisible: {
        color: "semantic.link.primary",
        outline: "1px solid token(colors.semantic.text.primary)",
        borderRadius: "radius.2"
      }
    },
    container: {
      display: "flex",
      alignItems: "center",
      gap: "space.4"
    },
    text: {
      _hover: {
        transition: "background 0.3s cubic-bezier(0.7, 0, 0.3, 1)"
        // TODO: tokens
      }
    },
    leadingIcon: {
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      marginRight: "space.4"
    },
    trailingIcon: {
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      width: "12px",
      height: "12px"
    }
  },
  variants: {
    size: {
      small: {
        text: {
          textStyle: "Semantics/UI Label/Small"
        }
      },
      regular: {
        text: {
          textStyle: "Semantics/UI Label/Regular"
        }
      },
      medium: {
        text: {
          textStyle: "Semantics/UI Label/Medium"
        }
      },
      large: {
        text: {
          textStyle: "Semantics/UI Label/Large"
        }
      }
    },
    linkType: {
      primary: {
        root: {
          color: "semantic.link.primary",
          _hover: {
            color: "core.purps.800"
          },
          _focusVisible: {
            color: "core.purps.800"
          }
        }
      },
      positive: {
        root: {
          color: "semantic.link.positive",
          _hover: {
            color: "core.okaygreen.800"
          },
          _focusVisible: {
            color: "core.okaygreen.800"
          }
        }
      },
      destructive: {
        root: {
          color: "semantic.link.destructive",
          _hover: {
            color: "core.warningred.700"
          },
          _focusVisible: {
            color: "core.warningred.700"
          }
        }
      },
      candy: {
        root: {
          color: "semantic.link.candy",
          _hover: {
            color: "core.candy.700"
          },
          _focusVisible: {
            color: "core.candy.700"
          }
        }
      },
      linkblue: {
        root: {
          color: "semantic.link.linkblue",
          _hover: {
            color: "core.linkblue.800"
          },
          _focusVisible: {
            color: "core.linkblue.800"
          }
        }
      },
      warning: {
        root: {
          color: "semantic.link.warning",
          _hover: {
            color: "semantic.link.warning"
          },
          _focusVisible: {
            color: "semantic.link.warning"
          }
        }
      },
      oceanblue: {
        root: {
          color: "core.oceanblue.800",
          _hover: {
            color: "core.oceanblue.900"
          },
          _focusVisible: {
            color: "core.oceanblue.900"
          }
        }
      },
      white: {
        root: {
          color: "core.grey.400",
          _hover: {
            color: "semantic.text.white"
          },
          _focusVisible: {
            color: "semantic.text.white"
          }
        }
      }
    },
    underline: {
      true: {
        text: {
          textUnderlineOffset: "token(spacing.space.6)",
          textDecorationLine: "underline"
        }
      },
      false: {
        text: {
          textDecoration: "none"
        }
      }
    },
    heavy: {
      true: {},
      false: {}
    }
  },
  compoundVariants: [
    // different sizes with different weights
    {
      size: "small",
      heavy: true,
      css: {
        text: {
          textStyle: "Semantics/UI Label/Small (Heavy)"
        }
      }
    },
    {
      size: "regular",
      heavy: true,
      css: {
        text: {
          textStyle: "Semantics/UI Label/Regular (Heavy)"
        }
      }
    },
    {
      size: "medium",
      heavy: true,
      css: {
        text: {
          textStyle: "Semantics/UI Label/Medium (Heavy)"
        }
      }
    },
    {
      size: "large",
      heavy: true,
      css: {
        text: {
          textStyle: "Semantics/UI Label/Large (Heavy)"
        }
      }
    },
    // icon with different CTA sizes.
    {
      size: ["small", "regular"],
      css: {
        leadingIcon: {
          width: "12px",
          height: "12px"
        }
      }
    },
    {
      size: ["medium", "large"],
      css: {
        leadingIcon: {
          width: "16px",
          height: "16px"
        }
      }
    }
  ],
  defaultVariants: {
    size: "medium",
    underline: true,
    heavy: false
  }
});

// atoms/link/index.tsx
function Link(_a) {
  var _b = _a, {
    as,
    size = "medium",
    text,
    linkType,
    underline = true,
    heavy = false,
    leadingIcon,
    trailingIcon,
    className
  } = _b, rest = __objRest(_b, [
    "as",
    "size",
    "text",
    "linkType",
    "underline",
    "heavy",
    "leadingIcon",
    "trailingIcon",
    "className"
  ]);
  const Component = as === "anchor" ? "a" : as;
  const { cssRaw, restProps } = splitCssProps(rest);
  const linkStyles = linkRecipe.raw({
    size,
    linkType,
    heavy,
    underline
  });
  const componentProps = __spreadValues({
    className: cx4(css6(linkStyles.root, cssRaw), className),
    "aria-label": text
  }, restProps);
  return <Component {...componentProps} tabIndex={0}>
			<Box className={css6(linkStyles.container)}>
				{leadingIcon && <Box className={css6(linkStyles.leadingIcon)}>
						{leadingIcon}
					</Box>}
				<Text as='span' className={css6(linkStyles.text)}>
					{text}
				</Text>
				{trailingIcon && <Box className={css6(linkStyles.trailingIcon)}>
						{trailingIcon}
					</Box>}
			</Box>
		</Component>;
}
__name(Link, "Link");

// components/dropdown/DropdownRoot/index.tsx
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
var DropdownRoot = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { children } = _b, props = __objRest(_b, ["children"]);
  return <DropdownMenuPrimitive.Root {...props}>
			{children}
		</DropdownMenuPrimitive.Root>;
}, "DropdownRoot");

// components/dropdown/DropdownTrigger/index.tsx
import * as DropdownMenuPrimitive2 from "@radix-ui/react-dropdown-menu";
var DropdownTrigger = /* @__PURE__ */ __name((_a) => {
  var _b = _a, {
    children,
    className
  } = _b, props = __objRest(_b, [
    "children",
    "className"
  ]);
  return <DropdownMenuPrimitive2.Trigger className={className} {...props}>
			{children}
		</DropdownMenuPrimitive2.Trigger>;
}, "DropdownTrigger");

// components/dropdown/DropdownContent/index.tsx
import { useCallback, useRef as useRef3 } from "react";
import * as DropdownMenuPrimitive3 from "@radix-ui/react-dropdown-menu";
import { cx as cx5 } from "@headout/pixie/css";

// components/dropdown/DropdownContent/styles.ts
import { css as css7 } from "@headout/pixie/css";
var contentStyles = css7({
  backgroundColor: "semantic.surface.light.white",
  borderRadius: "radius.8",
  paddingBlock: "space.12",
  paddingLeft: "space.12",
  paddingRight: "space.8",
  boxShadow: "shadow.level.2",
  zIndex: 100,
  minWidth: "232px",
  maxWidth: "924px",
  maxHeight: "432px",
  overflowY: "auto",
  _scrollbar: {
    width: "20px"
  },
  _scrollbarTrack: {
    border: "solid 4px transparent"
  },
  _scrollbarThumb: {
    backgroundColor: "semantic.dividers.dark",
    borderRadius: "16px",
    border: "solid 8px white"
  },
  animationDuration: "200ms",
  animationTimingFunction: "cubic-bezier(0.7, 0, 0.3, 1)",
  willChange: "transform, opacity",
  '&[data-state="open"]': {
    animationName: "scaleIn"
  },
  '&[data-state="closed"]': {
    animationName: "scaleOut"
  },
  '&[data-show-scroll="false"]': {
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none"
    }
  }
});

// components/dropdown/DropdownContent/index.tsx
var DropdownContent = /* @__PURE__ */ __name((_a) => {
  var _b = _a, {
    children,
    side = "bottom",
    align = "center",
    sideOffset = 8,
    className,
    onPointerDownOutside,
    onCloseAutoFocus,
    onPointerDown
  } = _b, props = __objRest(_b, [
    "children",
    "side",
    "align",
    "sideOffset",
    "className",
    "onPointerDownOutside",
    "onCloseAutoFocus",
    "onPointerDown"
  ]);
  const isCloseFromMouse = useRef3(false);
  const handlePointerDownOutside = useCallback(
    (e) => {
      isCloseFromMouse.current = true;
      onPointerDownOutside == null ? void 0 : onPointerDownOutside(e);
    },
    [onPointerDownOutside]
  );
  const handlePointerDown = useCallback(
    (e) => {
      isCloseFromMouse.current = true;
      onPointerDown == null ? void 0 : onPointerDown(e);
    },
    [onPointerDown]
  );
  const handleCloseAutoFocus = useCallback(
    (e) => {
      if (onCloseAutoFocus) {
        return onCloseAutoFocus(e);
      }
      if (!isCloseFromMouse.current) {
        return;
      }
      e.preventDefault();
      isCloseFromMouse.current = false;
    },
    [onCloseAutoFocus]
  );
  return <DropdownMenuPrimitive3.Portal>
			<DropdownMenuPrimitive3.Content
    className={cx5(contentStyles, className)}
    sideOffset={sideOffset}
    side={side}
    align={align}
    {...props}
    data-show-scroll={props.showScrollbar ? "true" : "false"}
    onCloseAutoFocus={handleCloseAutoFocus}
    onPointerDownOutside={handlePointerDownOutside}
    onPointerDown={handlePointerDown}
  >
				{children}
			</DropdownMenuPrimitive3.Content>
		</DropdownMenuPrimitive3.Portal>;
}, "DropdownContent");

// components/dropdown/DropdownItem/index.tsx
import * as DropdownMenuPrimitive4 from "@radix-ui/react-dropdown-menu";
import { cx as cx6 } from "@headout/pixie/css";

// components/dropdown/DropdownItem/styles.ts
import { css as css8 } from "@headout/pixie/css";
var dropdownItemStyles = css8({
  // Do not show the focus ring on the dropdown item, when hovering over it
  _hover: {
    _focusVisible: {
      outline: "none"
    }
  }
});

// components/dropdown/DropdownItem/index.tsx
var DropdownItem = /* @__PURE__ */ __name((_a) => {
  var _b = _a, {
    children,
    className
  } = _b, props = __objRest(_b, [
    "children",
    "className"
  ]);
  return <DropdownMenuPrimitive4.Item
    className={cx6(dropdownItemStyles, className)}
    {...props}
  >
			{children}
		</DropdownMenuPrimitive4.Item>;
}, "DropdownItem");

// components/dropdown/DropdownSeparator/index.tsx
import { cx as cx7 } from "@headout/pixie/css";
import * as DropdownMenuPrimitive5 from "@radix-ui/react-dropdown-menu";

// components/dropdown/DropdownSeparator/styles.ts
import { css as css9 } from "@headout/pixie/css";
var separatorStyles = css9({
  height: "1px",
  backgroundColor: "semantic.dividers.light",
  margin: "space.4"
});

// components/dropdown/DropdownSeparator/index.tsx
var DropdownSeparator = /* @__PURE__ */ __name((_a) => {
  var _b = _a, {
    className
  } = _b, props = __objRest(_b, [
    "className"
  ]);
  return <DropdownMenuPrimitive5.Separator
    className={cx7(separatorStyles, className)}
    {...props}
  />;
}, "DropdownSeparator");

// components/dropdown/index.tsx
import { DropdownMenuPortal } from "@radix-ui/react-dropdown-menu";
var Dropdown = {
  Root: DropdownRoot,
  Trigger: DropdownTrigger,
  Portal: DropdownMenuPortal,
  Content: DropdownContent,
  Item: DropdownItem,
  Separator: DropdownSeparator
};

// components/accordion/accordionItem.tsx
import { useEffect as useEffect2, useState } from "react";

// atoms/icon/index.tsx
import IconMap from "@headout/onix/web/mapper";
var Icon = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { name, ariaLabel, className } = _b, rest = __objRest(_b, ["name", "ariaLabel", "className"]);
  const IconComponent = IconMap[name];
  return <IconComponent
    aria-label={ariaLabel}
    aria-hidden={!ariaLabel}
    className={className}
    {...rest}
  />;
}, "Icon");

// components/accordion/styles.ts
import { sva as sva3 } from "@headout/pixie/css";
var accordionButtonRecipe = sva3({
  slots: ["listItem", "button", "icon", "chevron"],
  base: {
    listItem: {
      "&:not(:last-child)": {
        borderBottom: "1px solid",
        borderBottomColor: "semantic.dividers.dark"
      }
    },
    button: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      _focusVisible: {
        outline: "2px solid",
        outlineColor: "core.purps.800"
      }
    },
    chevron: {
      transitionProperty: "transform",
      transitionDuration: "0.2s",
      transitionTimingFunction: "cubic.standard"
    }
  },
  variants: {
    state: {
      default: {
        button: {
          cursor: "pointer",
          color: "semantic.text.grey.2"
        }
      },
      disabled: {
        button: {
          cursor: "not-allowed",
          color: "semantic.cta.disabled.primary"
        }
      }
    },
    size: {
      medium: {
        button: {
          paddingY: "space.24"
        },
        icon: {
          width: "20px",
          height: "20px"
        }
      },
      large: {
        button: {
          paddingY: "space.32"
        },
        icon: {
          width: "24px",
          height: "24px"
        }
      }
    },
    collapsed: {
      true: {
        chevron: {
          transform: "rotate(0deg)"
        }
      },
      false: {
        chevron: {
          transform: "rotate(180deg)"
        }
      }
    },
    flushAlignment: {
      true: {
        button: {
          paddingX: "0"
        }
      },
      false: {
        button: {
          paddingX: "space.20"
        }
      }
    }
  },
  compoundVariants: [
    {
      state: "default",
      flushAlignment: false,
      css: {
        button: {
          _hover: {
            background: "semantic.surface.light.grey.2"
          }
        }
      }
    }
  ]
});
var accordionContentRecipe = sva3({
  slots: ["container", "content"],
  base: {
    container: {
      display: "grid",
      gridTemplateRows: "0fr",
      transitionProperty: "grid-template-rows",
      transitionDuration: "0.2s",
      transitionTimingFunction: "cubic.standard"
    },
    content: {
      overflow: "hidden"
    }
  },
  variants: {
    collapsed: {
      true: {},
      false: {
        container: {
          gridTemplateRows: "1fr"
        }
      }
    },
    flushAlignment: {
      true: {
        content: {
          paddingX: "0"
        }
      },
      false: {
        content: {
          paddingX: "space.20"
        }
      }
    }
  }
});

// components/accordion/accordionItem.tsx
var AccordionItem = /* @__PURE__ */ __name(({
  title,
  icon,
  iconAriaLabel = "",
  size = "medium",
  disabled = false,
  collapsed = true,
  children,
  flushAlignment = false,
  onClick
}) => {
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  useEffect2(() => {
    setIsCollapsed(collapsed);
  }, [collapsed]);
  const handleClick = /* @__PURE__ */ __name(() => {
    setIsCollapsed((prev) => !prev);
    onClick == null ? void 0 : onClick();
  }, "handleClick");
  const {
    listItem,
    button,
    icon: iconStyles,
    chevron
  } = accordionButtonRecipe({
    size,
    state: disabled ? "disabled" : "default",
    collapsed: isCollapsed,
    flushAlignment
  });
  const { container, content } = accordionContentRecipe({
    collapsed: isCollapsed,
    flushAlignment
  });
  return <li className={listItem}>
			<button
    type='button'
    disabled={disabled}
    aria-expanded={!isCollapsed}
    className={button}
    onClick={handleClick}
    tabIndex={0}
  >
				<Box display={"flex"} alignItems={"center"} gap={"space.12"}>
					{icon && <Box
    as={"span"}
    role={"img"}
    aria-label={iconAriaLabel}
    className={iconStyles}
  >
							{icon}
						</Box>}

					<Text
    as='span'
    textStyle={size === "medium" ? "Semantics/Heading/Medium" : "Semantics/Heading/Large"}
  >
						{title}
					</Text>
				</Box>

				<Icon
    name='ChevronDown'
    className={chevron}
    aria-label='Toggle Accordion'
    width={18}
    height={16}
  />
			</button>
			<div className={container}>
				<div className={content}>{children}</div>
			</div>
		</li>;
}, "AccordionItem");

// components/accordion/index.tsx
var Accordion = /* @__PURE__ */ __name(({ children }) => {
  return <ul>{children}</ul>;
}, "Accordion");

// atoms/checkbox/index.tsx
import { useEffect as useEffect3, useState as useState2 } from "react";
import { cx as cx8 } from "@headout/pixie/css";

// ../../.storybook/assets/index.tsx
var Tick = /* @__PURE__ */ __name(() => {
  return <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
			<path
    d='M14 3.66663L5.75 12.3333L2 8.3939'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    strokeDasharray='22'
    strokeDashoffset='-20'
  />
		</svg>;
}, "Tick");

// atoms/checkbox/styles.ts
import { css as css10, cva as cva2, sva as sva4 } from "@headout/pixie/css";
var hiddenInput = css10({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: "1px",
  width: "1px",
  overflow: "hidden",
  position: "absolute",
  whiteSpace: "nowrap",
  _focusVisible: {
    "& + span > #focus-border": {
      display: "block"
    }
  }
});
var checkboxRecipe = sva4({
  slots: ["container", "box", "focusBorder"],
  base: {
    container: {
      display: "flex",
      justifyContent: "center",
      cursor: "pointer",
      width: "max-content"
    },
    box: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "semantic.surface.light.white",
      borderRadius: "radius.2",
      border: "1px solid token(colors.core.grey.400)",
      color: "core.primary.white",
      _active: {
        transform: "scale(0.95)"
      }
    },
    focusBorder: {
      display: "none",
      position: "absolute",
      top: "-2px",
      left: "-2px",
      outline: "1px solid token(colors.semantic.surface.dark.primary.2)"
    }
  },
  variants: {
    size: {
      small: {
        box: {
          width: "20px",
          height: "20px",
          "& > svg": {
            width: "16px",
            height: "16px"
          }
        },
        focusBorder: {
          width: "24px",
          height: "24px",
          borderRadius: "3px"
        }
      },
      large: {
        box: {
          width: "24px",
          height: "24px",
          borderRadius: "radius.4",
          "& > svg": {
            width: "18px",
            height: "18px"
          }
        },
        focusBorder: {
          width: "28px",
          height: "28px",
          borderRadius: "radius.6"
        }
      }
    },
    state: {
      default: {},
      disabled: {
        container: {
          cursor: "not-allowed"
        },
        box: {
          backgroundColor: "semantic.cta.disabled.tertiary",
          border: "1px solid token(colors.core.grey.400)",
          _active: {
            transform: "none"
          }
        }
      },
      error: {
        box: {
          backgroundColor: "semantic.surface.light.white",
          border: "1px solid token(colors.semantic.border.destructive)",
          _active: {
            transform: "none"
          }
        }
      }
    },
    checked: {
      yes: {
        box: {
          backgroundColor: "core.primary.purps",
          border: "none",
          _hover: {
            backgroundColor: "semantic.surface.dark.primary.3"
          },
          _active: {
            backgroundColor: "core.purps.700"
          },
          "& > svg > path": {
            strokeDashoffset: 0
          }
        }
      },
      partial: {
        box: {
          backgroundColor: "core.primary.purps",
          border: "none",
          _hover: {
            backgroundColor: "semantic.surface.dark.primary.3"
          },
          _active: {
            backgroundColor: "core.purps.700"
          }
        }
      },
      no: {}
    },
    labelAlignment: {
      trailing: {
        container: { gap: "space.12" }
      },
      leading: {
        container: { gap: "space.16" }
      }
    },
    hasCheckAnimation: {
      true: {},
      false: {
        box: {
          "& > svg > path": {
            transition: "none"
          }
        }
      }
    },
    hasTextAndSubtext: {
      true: {
        container: {
          alignItems: "flex-start"
        }
      },
      false: {
        container: {
          alignItems: "center"
        }
      }
    },
    variant: {
      default: {
        container: {
          padding: "token(spacing.space.12) token(spacing.space.8)"
        }
      },
      unstyled: {}
    }
  },
  compoundVariants: [
    {
      size: "small",
      hasCheckAnimation: true,
      css: {
        box: {
          "& > svg > path": {
            transition: "stroke-dashoffset 0.4s cubic-bezier(0.7, -0.3, 0.3, 1.3)"
            //TODO: tokens
          }
        }
      }
    },
    {
      size: "large",
      hasCheckAnimation: true,
      css: {
        box: {
          "& > svg > path": {
            transition: "stroke-dashoffset 0.45s cubic-bezier(0.7, -0.3, 0.3, 1.3)"
            //TODO: tokens
          }
        }
      }
    },
    {
      state: "default",
      checked: "no",
      css: {
        container: {
          _hover: {
            '& [role="checkbox"]': {
              border: "1px solid token(colors.core.primary.purps)"
            }
          }
        }
      }
    },
    {
      state: "default",
      checked: "no",
      variant: "default",
      css: {
        container: {
          _hover: {
            backgroundColor: "semantic.surface.light.grey.2"
          },
          _active: {
            backgroundColor: "semantic.surface.light.grey.2"
          }
        }
      }
    }
  ],
  defaultVariants: {
    size: "small",
    state: "default",
    checked: "no",
    labelAlignment: "trailing",
    hasCheckAnimation: true,
    hasTextAndSubtext: false
  }
});
var labelIcon = cva2({
  base: {},
  variants: {
    size: {
      small: {
        "& > svg": {
          width: "16px",
          height: "16px"
        }
      },
      large: {
        "& > svg": {
          width: "20px",
          height: "20px"
        }
      }
    }
  },
  defaultVariants: {
    size: "small"
  }
});

// atoms/checkbox/checkboxLabel.tsx
var CheckboxLabel = /* @__PURE__ */ __name(({
  text,
  subtext,
  size = "small",
  icon,
  iconAriaLabel
}) => {
  const hasTextAndSubtext = !!(text && subtext);
  return <Box
    as='span'
    display='flex'
    alignItems={hasTextAndSubtext ? "flex-start" : "center"}
    {...icon && { gap: "space.8" }}
  >
			{icon && <Box
    color='semantic.text.grey.2'
    {...hasTextAndSubtext && { paddingTop: "space.2" }}
  >
					<Box
    as='span'
    role='img'
    aria-label={iconAriaLabel}
    aria-labelledby='checkbox-label'
    className={labelIcon({ size })}
  >
						{icon}
					</Box>
				</Box>}
			<Box
    as='span'
    id='checkbox-label'
    display='flex'
    flexDirection='column'
    {...hasTextAndSubtext && { gap: "space.4" }}
  >
				{text && <Text
    as='span'
    textStyle={size === "large" ? "Semantics/UI Label/Large (Heavy)" : "Semantics/UI Label/Medium (Heavy)"}
    color='semantic.text.grey.1'
  >
						{text}
					</Text>}
				{subtext && <Text
    as='span'
    textStyle={size === "large" ? "Semantics/UI Label/Medium" : "Semantics/UI Label/Regular"}
    color='semantic.text.grey.3'
  >
						{subtext}
					</Text>}
			</Box>
		</Box>;
}, "CheckboxLabel");
var CheckboxLabel_default = CheckboxLabel;

// atoms/checkbox/index.tsx
var Checkbox = /* @__PURE__ */ __name(({
  id,
  form,
  name,
  value,
  size = "small",
  state = "default",
  defaultChecked = "no",
  onCheckedChange,
  text,
  subtext,
  labelAlignment = "trailing",
  icon,
  iconAriaLabel,
  hasCheckAnimation = true,
  className,
  variant = "default",
  customLabel
}) => {
  const [checked, setChecked] = useState2(defaultChecked);
  const hasLeadingLabel = labelAlignment === "leading" && (text || subtext);
  const hasTrailingLabel = labelAlignment === "trailing" && (text || subtext);
  useEffect3(() => {
    if (defaultChecked !== checked) setChecked(defaultChecked);
    if (state !== "default") setChecked("no");
  }, [defaultChecked, state]);
  const handleChange = /* @__PURE__ */ __name(() => {
    if (state === "disabled" || state === "error") return;
    if (checked === "yes") {
      setChecked("no");
      onCheckedChange == null ? void 0 : onCheckedChange("no");
    } else {
      setChecked("yes");
      onCheckedChange == null ? void 0 : onCheckedChange("yes");
    }
  }, "handleChange");
  const checkboxStyles = checkboxRecipe({
    size,
    state,
    checked,
    labelAlignment,
    hasCheckAnimation,
    hasTextAndSubtext: !!(text && subtext),
    variant
  });
  const isChecked = checked === "yes" || checked === "partial";
  return <label htmlFor={id} className={cx8(checkboxStyles.container, className)}>
			<input
    type='checkbox'
    id={id}
    {...form && { form }}
    {...name && { name }}
    {...value && { value }}
    onChange={handleChange}
    checked={isChecked}
    disabled={state === "disabled" || state === "error"}
    className={hiddenInput}
  />
			{hasLeadingLabel && <CheckboxLabel_default
    text={text}
    subtext={subtext}
    size={size}
    icon={icon}
  />}
			<Box as='span' position='relative'>
				<Box
    as='span'
    id='focus-border'
    aria-hidden={true}
    className={checkboxStyles.focusBorder}
  />
				<Box
    id='checkbox-box'
    as='span'
    role='checkbox'
    aria-hidden={true}
    className={checkboxStyles.box}
  >
					{checked === "partial" ? <Icon name='Minus' width={16} height={16} /> : <Tick />}
				</Box>
			</Box>
			{hasTrailingLabel && <CheckboxLabel_default
    text={text}
    subtext={subtext}
    size={size}
    icon={icon}
    iconAriaLabel={iconAriaLabel}
  />}
			{!hasTrailingLabel && !hasLeadingLabel && customLabel ? customLabel : null}
		</label>;
}, "Checkbox");

// components/filters/index.tsx
import { forwardRef as forwardRef3 } from "react";

// components/filters/styles.ts
import { sva as sva5 } from "@headout/pixie/css";
var chipSelectorRecipe = sva5({
  slots: ["button", "container", "icon", "count"],
  base: {
    button: {
      width: "fit-content",
      border: "1px solid",
      _hover: {
        cursor: "pointer"
      },
      _focusVisible: {
        outline: "none",
        boxShadow: "0 0 0 2px token(colors.semantic.surface.light.white), 0 0 0 3px token(colors.semantic.surface.dark.primary.2)"
      }
    },
    container: {
      display: "flex",
      alignItems: "center"
    },
    count: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "radius.50p",
      backgroundColor: "semantic.surface.light.white",
      width: "0",
      height: "0",
      transition: "width 0.2s, height 0.2s"
    }
  },
  variants: {
    size: {
      small: {
        button: {
          paddingX: "space.12",
          paddingY: "space.6",
          borderRadius: "radius.20"
        },
        icon: {
          "& svg": {
            width: "12px",
            height: "12px"
          }
        },
        container: {
          gap: "space.6"
        },
        count: {
          alignItems: "flex-start"
        }
      },
      large: {
        button: {
          paddingX: "space.12",
          paddingY: "space.8",
          borderRadius: "24px"
        },
        icon: {
          "& svg": {
            width: "16px",
            height: "16px"
          }
        },
        container: {
          gap: "space.6"
        }
      }
    },
    state: {
      default: {
        button: {
          borderColor: "semantic.text.grey.1",
          color: "semantic.text.grey.1",
          backgroundColor: "semantic.surface.light.white",
          _hover: {
            backgroundColor: "core.purps.10",
            borderColor: "core.purps.200"
          },
          _active: {
            backgroundColor: "semantic.surface.light.primary.2",
            borderColor: "semantic.border.primary",
            color: "semantic.text.primary",
            transform: "scale(0.95)",
            transition: "transform 0.2s cubic-bezier(0.7, 0, 0.3, 1)"
          }
        }
      },
      disabled: {
        button: {
          borderColor: "semantic.border.disabled.2",
          color: "semantic.text.disabled",
          _hover: {
            cursor: "not-allowed"
          }
        }
      },
      applied: {
        button: {
          borderColor: "semantic.cta.primary",
          backgroundColor: "semantic.cta.primary",
          color: "semantic.text.white"
        }
      }
    },
    isIconOnly: {
      true: {
        button: {
          borderRadius: "radius.50p"
        }
      },
      false: {}
    },
    isIconCounterOnly: {
      true: {},
      false: {}
    },
    countVisible: {
      true: {
        count: {
          visibility: "visible",
          opacity: 1,
          marginLeft: "0"
        }
      },
      false: {
        count: {
          visibility: "hidden",
          opacity: 0,
          marginLeft: "-space.6"
        }
      }
    }
  },
  compoundVariants: [
    {
      state: "applied",
      size: "small",
      css: {
        button: {
          paddingY: "space.6"
        },
        container: {
          paddingY: "space.1"
        }
      }
    },
    {
      isIconOnly: true,
      size: "small",
      css: {
        button: {
          paddingX: "space.10",
          paddingY: "space.10"
        },
        container: {
          paddingY: "space.0"
        }
      }
    },
    {
      isIconCounterOnly: true,
      size: "small",
      css: {
        button: {
          gap: "space.4",
          paddingX: "space.8",
          paddingY: "space.6"
        },
        container: {
          paddingX: "space.1"
        }
      }
    },
    {
      size: "large",
      isIconOnly: true,
      css: {
        button: {
          paddingY: "space.12"
        }
      }
    },
    {
      size: "large",
      state: "applied",
      css: {
        button: {
          paddingY: "space.8"
        },
        container: {
          paddingY: "space.1"
        }
      }
    },
    {
      isIconOnly: true,
      size: "large",
      css: {
        button: {
          paddingX: "space.12",
          paddingY: "space.12"
        },
        container: {
          paddingY: "space.0"
        }
      }
    },
    {
      isIconCounterOnly: true,
      size: "large",
      css: {
        button: {
          gap: "space.4",
          paddingX: "space.8",
          paddingY: "space.8"
        },
        container: {
          paddingX: "space.1",
          paddingY: "space.1"
        }
      }
    },
    {
      countVisible: true,
      size: "small",
      css: {
        count: {
          width: "18px",
          height: "18px"
        }
      }
    },
    {
      countVisible: true,
      size: "large",
      css: {
        count: {
          width: "22px",
          height: "22px"
        }
      }
    }
  ]
});
var seatmapFilterRecipe = sva5({
  slots: ["button", "container", "priceContainer", "price", "cutPrice"],
  base: {
    button: {
      border: "1px solid",
      width: "fit-content",
      height: "fit-content",
      _hover: {
        cursor: "pointer"
      },
      _focusVisible: {
        outline: "none",
        boxShadow: "0 0 0 2px token(colors.semantic.surface.light.white), 0 0 0 3px token(colors.semantic.surface.dark.primary.2)"
      }
    },
    container: {
      display: "flex",
      alignItems: "center"
    }
  },
  variants: {
    state: {
      default: {
        button: {
          backgroundColor: "semantic.surface.light.white",
          borderColor: "semantic.border.disabled.2",
          _hover: {
            backgroundColor: "core.purps.10",
            borderColor: "core.purps.200"
          },
          _active: {
            transform: "scale(0.95)",
            transition: "transform 0.2s cubic-bezier(0.7, 0, 0.3, 1)"
          }
        },
        price: {
          color: "semantic.text.grey.1"
        },
        cutPrice: {
          color: "semantic.text.grey.3"
        }
      },
      applied: {
        button: {
          backgroundColor: "semantic.surface.light.primary.2",
          borderColor: "semantic.border.primary"
        },
        price: {
          color: "semantic.text.primary"
        },
        cutPrice: {
          color: "semantic.text.grey.2"
        }
      }
    },
    size: {
      small: {
        button: {
          paddingLeft: "space.8",
          paddingRight: "space.12",
          paddingY: "space.6",
          borderRadius: "radius.20",
          "& svg": {
            width: "18px",
            height: "18px"
          }
        },
        container: {
          gap: "space.6",
          paddingY: "space.1"
        }
      },
      large: {
        button: {
          paddingLeft: "space.10",
          paddingRight: "space.12",
          paddingY: "space.8",
          borderRadius: "24px",
          "& svg": {
            width: "22px",
            height: "22px"
          }
        },
        container: {
          gap: "space.6",
          paddingY: "space.1"
        }
      }
    },
    hasCutPrice: {
      true: {},
      false: {
        priceContainer: {
          marginBottom: "space.2"
        }
      }
    }
  },
  compoundVariants: [
    {
      size: "small",
      hasCutPrice: true,
      css: {
        button: {
          paddingLeft: "space.12",
          paddingRight: "space.16",
          borderRadius: "radius.20"
        },
        container: {
          paddingY: "space.0"
        }
      }
    },
    {
      size: "large",
      hasCutPrice: true,
      css: {
        button: {
          paddingLeft: "space.12",
          paddingRight: "space.16",
          paddingY: "space.6",
          borderRadius: "24px"
        },
        container: {
          paddingY: "space.0"
        }
      }
    }
  ]
});

// components/filters/chipSelector.tsx
var ChipSelector = /* @__PURE__ */ __name(({
  state,
  text,
  size,
  leadingIcon,
  trailingIcon,
  selectedCount,
  onClick,
  componentRef,
  isDropdownOpen
}) => {
  const isIconOnly = Boolean(
    (leadingIcon || trailingIcon) && !text && !selectedCount
  );
  const isIconCounterOnly = Boolean(
    (leadingIcon || trailingIcon) && !text && selectedCount
  );
  const showSelectedCount = !!selectedCount && !trailingIcon && state === "applied";
  const styles = chipSelectorRecipe({
    state,
    size,
    isIconOnly,
    isIconCounterOnly,
    countVisible: !!showSelectedCount
  });
  return <button
    ref={componentRef}
    title={isIconOnly ? "Filter" : void 0}
    className={styles.button}
    disabled={state === "disabled"}
    aria-disabled={state === "disabled"}
    onClick={onClick}
    tabIndex={isDropdownOpen ? -1 : 0}
  >
			<div className={styles.container}>
				{leadingIcon && <span className={styles.icon}>{leadingIcon}</span>}
				{text && <Text
    as={"span"}
    textStyle={size === "small" ? "Semantics/UI Label/Regular (Heavy)" : "Semantics/UI Label/Large (Heavy)"}
    marginTop={"space.1"}
    marginBottom={"space.3"}
  >
						{text}
					</Text>}
				{trailingIcon && <span className={styles.icon}>{trailingIcon}</span>}
				<span className={styles.count}>
					<Text
    color={"semantic.cta.primary"}
    textStyle={size === "small" ? "Semantics/UI Label/Small (Heavy)" : "Semantics/UI Label/Regular (Heavy)"}
    marginBottom={size === "small" ? "0" : "space.2"}
  >
						{selectedCount}
					</Text>
				</span>
			</div>
		</button>;
}, "ChipSelector");

// components/filters/seatmapFilter.tsx
var SeatmapFilter = /* @__PURE__ */ __name(({
  componentRef,
  state = "default",
  price,
  cutPrice,
  size,
  dotColor,
  onClick
}) => {
  const styles = seatmapFilterRecipe({
    state,
    size,
    hasCutPrice: !!cutPrice
  });
  return <button ref={componentRef} onClick={onClick} className={styles.button}>
			<div className={styles.container}>
				<FilterDot color={dotColor} isSelected={state === "applied"} />
				<Box
    display={"flex"}
    flexDirection={"column"}
    className={styles.priceContainer}
  >
					{cutPrice && <Text
    as={"span"}
    align={"left"}
    marginLeft={"space.1"}
    textStyle={size === "small" ? "Semantics/UI Label/Strike/Extra Small" : "Semantics/UI Label/Strike/Small (Heavy)"}
    className={styles.cutPrice}
  >
							{cutPrice}
						</Text>}
					<Text
    as={"span"}
    textStyle={size === "small" ? "Semantics/UI Label/Regular (Heavy)" : "Semantics/UI Label/Large (Heavy)"}
    className={styles.price}
  >
						{price}
					</Text>
				</Box>
			</div>
		</button>;
}, "SeatmapFilter");
var FilterDot = /* @__PURE__ */ __name(({
  color,
  isSelected
}) => {
  return <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='18'
    fill='none'
    viewBox='0 0 18 18'
  >
			<g filter='url(#filter0_i_6728_1876)'>
				<path
    fill={color}
    d='M18 9A9 9 0 1 1 0 9a9 9 0 0 1 18 0'
  />
				<path
    stroke='#fff'
    strokeLinecap='round'
    strokeLinejoin='round'
    strokeWidth='2'
    d='M13.5 5.5 7.313 12 4.5 9.045'
    strokeDasharray={"22"}
    strokeDashoffset={isSelected ? "0" : "-20"}
    style={{
      transition: "stroke-dashoffset 0.4s cubic-bezier(0.7, -0.3, 0.3, 1.3)"
    }}
  />
			</g>
		</svg>;
}, "FilterDot");

// components/filters/index.tsx
var Filters = forwardRef3(/* @__PURE__ */ __name(function Filters2(_a, ref) {
  var _b = _a, { variant = "chip selector", size = "small" } = _b, props = __objRest(_b, ["variant", "size"]);
  return <>
			{variant === "chip selector" && <ChipSelector
    size={size}
    componentRef={ref}
    {...props}
  />}
			{variant === "seatmap filter" && <SeatmapFilter
    size={size}
    componentRef={ref}
    {...props}
  />}
		</>;
}, "Filters"));
export {
  Accordion,
  AccordionItem,
  Box,
  Button,
  Checkbox,
  Dropdown,
  Filters,
  Icon,
  Link,
  Text
};
